import { API_URL } from "../config/api";

// Interfaces based on API specification
interface ManufacturerInterface {
  id: string;
  name: string;
  description: string;
  website: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  country: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface WarrantyInterface {
  id: string;
  project_material_id: string;
  unit_id: string | null;
  manufacturer_id: string;
  warranty_start_date: string;
  warranty_end_date: string;
  warranty_provider: string;
  warranty_terms: string;
  coverage_details: string;
  serial_number: string;
  model: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  is_active_warranty: boolean;
  days_remaining: number;
  manufacturer: ManufacturerInterface;
}

interface WarrantyCreateData {
  project_material_id: string | null;
  unit_id: string | null;
  manufacturer_id: string;
  warranty_start_date: string;
  warranty_end_date: string;
  warranty_provider: string;
  warranty_terms: string;
  coverage_details: string;
  serial_number: string;
  model: string;
}

interface WarrantyUpdateData extends WarrantyCreateData {
  id: string;
}

interface WarrantiesResponse {
  total: number;
  page: number;
  size: number;
  results: WarrantyInterface[];
}

/**
 * Get warranties for a project
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {number} page - Page number for pagination
 * @param {number} size - Number of items per page
 * @param {string} manufacturerId - Optional manufacturer ID filter
 * @returns {Promise<WarrantiesResponse>} List of warranties
 */
export const getProjectWarranties = async (
  token: string,
  projectId: string,
  page: number = 1,
  size: number = 100,
  manufacturerId?: string
): Promise<WarrantiesResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    if (manufacturerId) {
      params.append('manufacturer_id', manufacturerId);
    }

    const response = await fetch(`${API_URL}/projects/${projectId}/warranties?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: WarrantiesResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error in getProjectWarranties:', error);
    throw new Error(error.message || 'Failed to fetch warranties');
  }
};

/**
 * Create a new warranty
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {WarrantyCreateData} warrantyData - Warranty data to create
 * @returns {Promise<string>} Created warranty ID
 */
export const createWarranty = async (
  token: string,
  projectId: string,
  warrantyData: WarrantyCreateData
): Promise<string> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/warranties`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(warrantyData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const warrantyId: string = await response.json();
    return warrantyId;
  } catch (error: any) {
    console.error('Error in createWarranty:', error);
    throw new Error(error.message || 'Failed to create warranty');
  }
};

/**
 * Update an existing warranty
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} warrantyId - Warranty ID
 * @param {WarrantyUpdateData} warrantyData - Updated warranty data
 * @returns {Promise<void>} Success response
 */
export const updateWarranty = async (
  token: string,
  projectId: string,
  warrantyId: string,
  warrantyData: WarrantyUpdateData
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/warranties/${warrantyId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(warrantyData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('Error in updateWarranty:', error);
    throw new Error(error.message || 'Failed to update warranty');
  }
};

/**
 * Delete a warranty
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} warrantyId - Warranty ID
 * @returns {Promise<string>} Success message
 */
export const deleteWarranty = async (
  token: string,
  projectId: string,
  warrantyId: string
): Promise<string> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/warranties/${warrantyId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const message: string = await response.json();
    return message;
  } catch (error: any) {
    console.error('Error in deleteWarranty:', error);
    throw new Error(error.message || 'Failed to delete warranty');
  }
};

// Export types
export type {
  WarrantyInterface,
  ManufacturerInterface,
  WarrantyCreateData,
  WarrantyUpdateData,
  WarrantiesResponse
}; 